import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import NavBar from "@/components/NavBar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { toast } from "@/hooks/use-toast";
import { useCampaign, useUpdateCampaign } from "@/hooks/useApi";
import {
  Save,
  ArrowLeft,
  ArrowRight,
  Sparkles,
  PenLine,
  Settings,
  Eye,
  Loader2,
  CheckCircle,
  Target,
  TrendingUp,
  DollarSign,
  Plus,
  Trash2,
  Image,
} from "lucide-react";

// Categories for campaigns
const categories = [
  "Productivity",
  "Food & Drink",
  "Finance",
  "Health",
  "Entertainment",
  "Education",
  "Social",
  "Travel",
  "Technology",
  "Business",
  "Lifestyle",
  "Other",
];

// Available banner images - category-specific
const bannerImages = [
  // Productivity
  {
    id: "productivity-workspace",
    name: "Modern Workspace",
    category: "Productivity",
    url: "https://images.unsplash.com/photo-1497366216548-37526070297c?auto=format&fit=crop&w=800&h=250",
    thumbnail:
      "https://images.unsplash.com/photo-1497366216548-37526070297c?auto=format&fit=crop&w=200&h=100",
  },
  {
    id: "productivity-desk",
    name: "Organized Desk",
    category: "Productivity",
    url: "https://images.unsplash.com/photo-1586281380349-632531db7ed4?auto=format&fit=crop&w=800&h=250",
    thumbnail:
      "https://images.unsplash.com/photo-1586281380349-632531db7ed4?auto=format&fit=crop&w=200&h=100",
  },

  // Food & Drink
  {
    id: "food-culinary",
    name: "Culinary Excellence",
    category: "Food & Drink",
    url: "https://images.unsplash.com/photo-1556909114-f6e7ad7d3136?auto=format&fit=crop&w=800&h=250",
    thumbnail:
      "https://images.unsplash.com/photo-1556909114-f6e7ad7d3136?auto=format&fit=crop&w=200&h=100",
  },
  {
    id: "food-restaurant",
    name: "Restaurant Ambiance",
    category: "Food & Drink",
    url: "https://images.unsplash.com/photo-1517248135467-4c7edcad34c4?auto=format&fit=crop&w=800&h=250",
    thumbnail:
      "https://images.unsplash.com/photo-1517248135467-4c7edcad34c4?auto=format&fit=crop&w=200&h=100",
  },

  // Finance
  {
    id: "finance-charts",
    name: "Financial Analytics",
    category: "Finance",
    url: "https://images.unsplash.com/photo-1551288049-bebda4e38f71?auto=format&fit=crop&w=800&h=250",
    thumbnail:
      "https://images.unsplash.com/photo-1551288049-bebda4e38f71?auto=format&fit=crop&w=200&h=100",
  },
  {
    id: "finance-banking",
    name: "Modern Banking",
    category: "Finance",
    url: "https://images.unsplash.com/photo-1560472354-b33ff0c44a43?auto=format&fit=crop&w=800&h=250",
    thumbnail:
      "https://images.unsplash.com/photo-1560472354-b33ff0c44a43?auto=format&fit=crop&w=200&h=100",
  },

  // Health
  {
    id: "health-medical",
    name: "Medical Technology",
    category: "Health",
    url: "https://images.unsplash.com/photo-1576091160399-112ba8d25d1f?auto=format&fit=crop&w=800&h=250",
    thumbnail:
      "https://images.unsplash.com/photo-1576091160399-112ba8d25d1f?auto=format&fit=crop&w=200&h=100",
  },
  {
    id: "health-wellness",
    name: "Wellness & Fitness",
    category: "Health",
    url: "https://images.unsplash.com/photo-1571019613454-1cb2f99b2d8b?auto=format&fit=crop&w=800&h=250",
    thumbnail:
      "https://images.unsplash.com/photo-1571019613454-1cb2f99b2d8b?auto=format&fit=crop&w=200&h=100",
  },

  // Entertainment
  {
    id: "entertainment-gaming",
    name: "Gaming & Entertainment",
    category: "Entertainment",
    url: "https://images.unsplash.com/photo-1542751371-adc38448a05e?auto=format&fit=crop&w=800&h=250",
    thumbnail:
      "https://images.unsplash.com/photo-1542751371-adc38448a05e?auto=format&fit=crop&w=200&h=100",
  },
  {
    id: "entertainment-music",
    name: "Music & Media",
    category: "Entertainment",
    url: "https://images.unsplash.com/photo-1493225457124-a3eb161ffa5f?auto=format&fit=crop&w=800&h=250",
    thumbnail:
      "https://images.unsplash.com/photo-1493225457124-a3eb161ffa5f?auto=format&fit=crop&w=200&h=100",
  },

  // Education
  {
    id: "education-learning",
    name: "Learning Environment",
    category: "Education",
    url: "https://images.unsplash.com/photo-1523240798132-8757214e76ba?auto=format&fit=crop&w=800&h=250",
    thumbnail:
      "https://images.unsplash.com/photo-1523240798132-8757214e76ba?auto=format&fit=crop&w=200&h=100",
  },
  {
    id: "education-online",
    name: "Online Education",
    category: "Education",
    url: "https://images.unsplash.com/photo-1522202176988-66273c2fd55f?auto=format&fit=crop&w=800&h=250",
    thumbnail:
      "https://images.unsplash.com/photo-1522202176988-66273c2fd55f?auto=format&fit=crop&w=200&h=100",
  },

  // Social
  {
    id: "social-community",
    name: "Community Connection",
    category: "Social",
    url: "https://images.unsplash.com/photo-1521737711867-e3b97375f902?auto=format&fit=crop&w=800&h=250",
    thumbnail:
      "https://images.unsplash.com/photo-1521737711867-e3b97375f902?auto=format&fit=crop&w=200&h=100",
  },
  {
    id: "social-networking",
    name: "Social Networking",
    category: "Social",
    url: "https://images.unsplash.com/photo-1551836022-d5d88e9218df?auto=format&fit=crop&w=800&h=250",
    thumbnail:
      "https://images.unsplash.com/photo-1551836022-d5d88e9218df?auto=format&fit=crop&w=200&h=100",
  },

  // Travel
  {
    id: "travel-destinations",
    name: "Travel Destinations",
    category: "Travel",
    url: "https://images.unsplash.com/photo-1488646953014-85cb44e25828?auto=format&fit=crop&w=800&h=250",
    thumbnail:
      "https://images.unsplash.com/photo-1488646953014-85cb44e25828?auto=format&fit=crop&w=200&h=100",
  },
  {
    id: "travel-adventure",
    name: "Adventure & Exploration",
    category: "Travel",
    url: "https://images.unsplash.com/photo-1506905925346-21bda4d32df4?auto=format&fit=crop&w=800&h=250",
    thumbnail:
      "https://images.unsplash.com/photo-1506905925346-21bda4d32df4?auto=format&fit=crop&w=200&h=100",
  },

  // Technology
  {
    id: "tech-innovation",
    name: "Tech Innovation",
    category: "Technology",
    url: "https://images.unsplash.com/photo-1518709268805-4e9042af2176?auto=format&fit=crop&w=800&h=250",
    thumbnail:
      "https://images.unsplash.com/photo-1518709268805-4e9042af2176?auto=format&fit=crop&w=200&h=100",
  },
  {
    id: "tech-coding",
    name: "Coding & Development",
    category: "Technology",
    url: "https://images.unsplash.com/photo-1461749280684-dccba630e2f6?auto=format&fit=crop&w=800&h=250",
    thumbnail:
      "https://images.unsplash.com/photo-1461749280684-dccba630e2f6?auto=format&fit=crop&w=200&h=100",
  },

  // Business
  {
    id: "business-corporate",
    name: "Corporate Excellence",
    category: "Business",
    url: "https://images.unsplash.com/photo-1556761175-b413da4baf72?auto=format&fit=crop&w=800&h=250",
    thumbnail:
      "https://images.unsplash.com/photo-1556761175-b413da4baf72?auto=format&fit=crop&w=200&h=100",
  },
  {
    id: "business-entrepreneur",
    name: "Entrepreneurship",
    category: "Business",
    url: "https://images.unsplash.com/photo-1559136555-9303baea8ebd?auto=format&fit=crop&w=800&h=250",
    thumbnail:
      "https://images.unsplash.com/photo-1559136555-9303baea8ebd?auto=format&fit=crop&w=200&h=100",
  },

  // Lifestyle
  {
    id: "lifestyle-modern",
    name: "Modern Lifestyle",
    category: "Lifestyle",
    url: "https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?auto=format&fit=crop&w=800&h=250",
    thumbnail:
      "https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?auto=format&fit=crop&w=200&h=100",
  },
  {
    id: "lifestyle-wellness",
    name: "Wellness Living",
    category: "Lifestyle",
    url: "https://images.unsplash.com/photo-1544367567-0f2fcb009e0b?auto=format&fit=crop&w=800&h=250",
    thumbnail:
      "https://images.unsplash.com/photo-1544367567-0f2fcb009e0b?auto=format&fit=crop&w=200&h=100",
  },

  // Other (Generic/Versatile)
  {
    id: "other-abstract",
    name: "Abstract Innovation",
    category: "Other",
    url: "https://images.unsplash.com/photo-1451187580459-43490279c0fa?auto=format&fit=crop&w=800&h=250",
    thumbnail:
      "https://images.unsplash.com/photo-1451187580459-43490279c0fa?auto=format&fit=crop&w=200&h=100",
  },
  {
    id: "other-creative",
    name: "Creative Vision",
    category: "Other",
    url: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?auto=format&fit=crop&w=800&h=250",
    thumbnail:
      "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?auto=format&fit=crop&w=200&h=100",
  },
];

const steps = [
  {
    id: 1,
    title: "Product & Market",
    description: "Core information & strategy",
    icon: Sparkles,
  },
  {
    id: 2,
    title: "Validation",
    description: "Research questions",
    icon: Target,
  },
  {
    id: 3,
    title: "Monetization",
    description: "Pricing strategy",
    icon: DollarSign,
  },
  {
    id: 4,
    title: "Configuration",
    description: "Campaign settings",
    icon: Settings,
  },
  { id: 5, title: "Review", description: "Final check & update", icon: Eye },
];

const EditCampaign = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);

  // Use React Query hooks
  const { data: campaign, isLoading, error } = useCampaign(id!);
  const updateCampaignMutation = useUpdateCampaign();

  const [campaignData, setCampaignData] = useState({
    title: "",
    tagline: "",
    description: "",
    problemStatement: "",
    solution: "",
    category: "",
    status: "DRAFT" as "DRAFT" | "ACTIVE" | "PAUSED" | "ARCHIVED" | "SUSPENDED",
    bannerImage: "",
    isPrivate: false,
    questions: [] as Array<{
      id?: string;
      text: string;
      type: string;
      options: string[];
      ratingScale?: string;
      ratingLabels?: string;
      customLabels?: string;
    }>,
    pricingOptions: [] as Array<{
      id?: string;
      price: string;
      description: string;
    }>,
  });

  // Update local state when campaign data is loaded
  useEffect(() => {
    if (campaign) {
      setCampaignData({
        title: campaign.title,
        tagline: campaign.tagline,
        description: campaign.description,
        problemStatement: campaign.problemStatement || "",
        solution: campaign.solution || "",
        category: campaign.category || "",
        status: campaign.status || "DRAFT",
        bannerImage: campaign.bannerImage || "",
        isPrivate: campaign.isPrivate || false,
        questions: (campaign.questions || []).map((q) => ({
          id: q.id,
          text: q.text,
          type: q.type.toLowerCase(),
          options: q.options || [],
          ratingScale: q.ratingScale?.toString() || "5",
          ratingLabels: q.ratingLabels || "numbers",
          customLabels: q.customLabels?.join(", ") || "",
        })),
        pricingOptions: campaign.pricingOptions || [],
      });
    }
  }, [campaign]);

  // Handle error
  useEffect(() => {
    if (error) {
      toast({
        title: "Error",
        description: "Failed to load campaign data. Please try again.",
        variant: "destructive",
      });
      navigate("/browse");
    }
  }, [error, navigate]);

  const handleInputChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >
  ) => {
    const { name, value } = e.target;
    setCampaignData((prev) => ({ ...prev, [name]: value }));
  };

  const handleNextStep = () => {
    if (currentStep < steps.length) {
      setCurrentStep((prev) => prev + 1);
      window.scrollTo(0, 0);
    }
  };

  const handlePrevStep = () => {
    if (currentStep > 1) {
      setCurrentStep((prev) => prev - 1);
      window.scrollTo(0, 0);
    }
  };

  // Helper functions for managing questions and pricing options
  const addQuestion = () => {
    setCampaignData({
      ...campaignData,
      questions: [
        ...campaignData.questions,
        {
          text: "",
          type: "text",
          options: [],
          ratingScale: "5",
          ratingLabels: "numbers",
          customLabels: "",
        },
      ],
    });
  };

  const removeQuestion = (index: number) => {
    const updatedQuestions = [...campaignData.questions];
    updatedQuestions.splice(index, 1);
    setCampaignData({ ...campaignData, questions: updatedQuestions });
  };

  const updateQuestion = (
    index: number,
    field: string,
    value: string | string[]
  ) => {
    const updatedQuestions = [...campaignData.questions];
    updatedQuestions[index] = { ...updatedQuestions[index], [field]: value };
    setCampaignData({ ...campaignData, questions: updatedQuestions });
  };

  const addOption = (questionIndex: number) => {
    const updatedQuestions = [...campaignData.questions];
    if (!updatedQuestions[questionIndex].options) {
      updatedQuestions[questionIndex].options = [];
    }
    updatedQuestions[questionIndex].options!.push("");
    setCampaignData({ ...campaignData, questions: updatedQuestions });
  };

  const removeOption = (questionIndex: number, optionIndex: number) => {
    const updatedQuestions = [...campaignData.questions];
    updatedQuestions[questionIndex].options!.splice(optionIndex, 1);
    setCampaignData({ ...campaignData, questions: updatedQuestions });
  };

  const updateOption = (
    questionIndex: number,
    optionIndex: number,
    value: string
  ) => {
    const updatedQuestions = [...campaignData.questions];
    updatedQuestions[questionIndex].options![optionIndex] = value;
    setCampaignData({ ...campaignData, questions: updatedQuestions });
  };

  const addPricingOption = () => {
    setCampaignData({
      ...campaignData,
      pricingOptions: [
        ...campaignData.pricingOptions,
        { price: "", description: "" },
      ],
    });
  };

  const removePricingOption = (index: number) => {
    const updatedOptions = [...campaignData.pricingOptions];
    updatedOptions.splice(index, 1);
    setCampaignData({ ...campaignData, pricingOptions: updatedOptions });
  };

  const updatePricingOption = (index: number, field: string, value: string) => {
    const updatedOptions = [...campaignData.pricingOptions];
    updatedOptions[index] = { ...updatedOptions[index], [field]: value };
    setCampaignData({ ...campaignData, pricingOptions: updatedOptions });
  };

  const handleSaveCampaign = async () => {
    // Transform form data to match backend DTO
    const updateData = {
      title: campaignData.title,
      tagline: campaignData.tagline,
      description: campaignData.description,
      problemStatement: campaignData.problemStatement,
      solution: campaignData.solution,
      category: campaignData.category,
      bannerImage: campaignData.bannerImage,
      isPrivate: campaignData.isPrivate,
      status: campaignData.status || "DRAFT",
      questions: campaignData.questions
        .filter((q) => q.text.trim() && q.type)
        .map((q, index) => {
          const baseQuestion = {
            text: q.text,
            type: q.type.toUpperCase() as
              | "TEXT"
              | "EMOJI"
              | "MULTIPLE"
              | "RATING",
            order: index + 1,
          };

          // Add options for multiple choice questions
          if (q.type === "multiple" && q.options && q.options.length >= 2) {
            return {
              ...baseQuestion,
              options: q.options.filter((opt) => opt.trim()),
            };
          }

          // Add rating configuration for rating questions
          if (q.type === "rating") {
            const ratingConfig: {
              text: string;
              type: "TEXT" | "EMOJI" | "MULTIPLE" | "RATING";
              order: number;
              ratingScale?: number;
              ratingLabels?: string;
              customLabels?: string[];
            } = { ...baseQuestion };

            if (q.ratingScale) {
              ratingConfig.ratingScale = parseInt(q.ratingScale);
            }

            if (q.ratingLabels === "custom" && q.customLabels) {
              ratingConfig.customLabels = q.customLabels
                .split(",")
                .map((label) => label.trim());
            } else if (q.ratingLabels) {
              ratingConfig.ratingLabels = q.ratingLabels;
            }

            return ratingConfig;
          }

          return baseQuestion;
        }),
      pricingOptions: campaignData.pricingOptions
        .filter((po) => po.price.trim() && po.description.trim())
        .map((po, index) => ({
          price: po.price,
          description: po.description,
          order: index + 1,
        })),
    };

    updateCampaignMutation.mutate(
      { id: id!, data: updateData },
      {
        onSuccess: () => {
          navigate(`/campaign/${id}`);
        },
      }
    );
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50/30 to-indigo-50/20">
        <NavBar />
        <div className="container mx-auto px-4 pt-24">
          <div className="max-w-3xl mx-auto">
            <div className="flex justify-center items-center h-64">
              <div className="text-center">
                <Loader2 className="w-12 h-12 animate-spin text-primary mx-auto mb-4" />
                <p className="text-slate-600">Loading your campaign data...</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <Card className="border-slate-200/60 shadow-lg">
            <CardHeader className="bg-gradient-to-r from-primary/5 to-accent/5">
              <CardTitle className="flex items-center gap-3">
                <div className="p-2 bg-primary/10 rounded-lg">
                  <Sparkles className="w-6 h-6 text-primary" />
                </div>
                Product & Market Strategy
              </CardTitle>
              <p className="text-slate-600 mt-2">
                Update your product details, market problem, and solution
                approach to ensure your campaign effectively communicates your
                value proposition.
              </p>
            </CardHeader>
            <CardContent className="p-8 space-y-8">
              {/* Product Information Section */}
              <div className="space-y-6">
                <h3 className="text-lg font-semibold text-slate-900 border-b pb-2">
                  Product Information
                </h3>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
                  <div className="space-y-2">
                    <label
                      htmlFor="title"
                      className="block text-sm font-medium text-slate-700"
                    >
                      Product Name
                    </label>
                    <Input
                      id="title"
                      name="title"
                      value={campaignData.title}
                      onChange={handleInputChange}
                      placeholder="Your product or service name"
                      className="border-slate-200 text-sm sm:text-base"
                      required
                    />
                    <p className="text-xs text-slate-500">
                      Choose a clear, memorable name that reflects your solution
                    </p>
                  </div>

                  <div className="space-y-2">
                    <label
                      htmlFor="tagline"
                      className="block text-sm font-medium text-slate-700"
                    >
                      Value Proposition
                    </label>
                    <Input
                      id="tagline"
                      name="tagline"
                      value={campaignData.tagline}
                      onChange={handleInputChange}
                      placeholder="The primary benefit your product delivers"
                      className="border-slate-200 text-sm sm:text-base"
                      required
                    />
                    <p className="text-xs text-slate-500">
                      Focus on the main value users will experience
                    </p>
                  </div>
                </div>

                <div className="space-y-2">
                  <label
                    htmlFor="description"
                    className="block text-sm font-medium text-slate-700"
                  >
                    Product Overview
                  </label>
                  <Textarea
                    id="description"
                    name="description"
                    value={campaignData.description}
                    onChange={handleInputChange}
                    placeholder="Describe your product's functionality, features, and how it works..."
                    className="min-h-24 border-slate-200"
                    required
                  />
                  <p className="text-xs text-slate-500">
                    Be specific about what your product does and how it delivers
                    value
                  </p>
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <label
                      htmlFor="category"
                      className="block text-sm font-medium text-slate-700"
                    >
                      Category
                    </label>
                    <select
                      id="category"
                      name="category"
                      className="w-full p-3 border border-slate-200 rounded-lg bg-white"
                      value={campaignData.category}
                      onChange={handleInputChange}
                    >
                      <option value="">Select a category</option>
                      {categories.map((category) => (
                        <option key={category} value={category}>
                          {category}
                        </option>
                      ))}
                    </select>
                    <p className="text-xs text-slate-500">
                      Choose the category that best describes your product
                    </p>
                  </div>

                  <div className="space-y-2">
                    <label
                      htmlFor="status"
                      className="block text-sm font-medium text-slate-700"
                    >
                      Campaign Status
                    </label>
                    <select
                      id="status"
                      name="status"
                      className="w-full p-3 border border-slate-200 rounded-lg bg-white"
                      value={campaignData.status}
                      onChange={handleInputChange}
                    >
                      <option value="DRAFT">Draft - Not yet active</option>
                      <option value="ACTIVE">
                        Active - Live and collecting feedback
                      </option>
                      <option value="PAUSED">
                        Paused - Temporarily paused
                      </option>
                      <option value="ARCHIVED">
                        Archived - No longer active
                      </option>
                    </select>
                    <p className="text-xs text-slate-500">
                      Control the visibility and activity of your campaign
                    </p>
                  </div>
                </div>
              </div>

              {/* Market Problem Section */}
              <div className="space-y-6">
                <h3 className="text-lg font-semibold text-slate-900 border-b pb-2">
                  Market Problem
                </h3>
                <div className="space-y-2">
                  <label
                    htmlFor="problemStatement"
                    className="block text-sm font-medium text-slate-700"
                  >
                    What problem does your product solve?
                  </label>
                  <Textarea
                    id="problemStatement"
                    name="problemStatement"
                    value={campaignData.problemStatement}
                    onChange={handleInputChange}
                    placeholder="What specific pain point or challenge does your product address in the market?"
                    className="min-h-24 border-slate-200"
                    required
                  />
                  <p className="text-xs text-slate-500">
                    Be specific about the problem, its impact, and why current
                    solutions fall short
                  </p>
                </div>
              </div>

              {/* Solution Section */}
              <div className="space-y-6">
                <h3 className="text-lg font-semibold text-slate-900 border-b pb-2">
                  Your Solution
                </h3>
                <div className="space-y-2">
                  <label
                    htmlFor="solution"
                    className="block text-sm font-medium text-slate-700"
                  >
                    How does your product solve this problem?
                  </label>
                  <Textarea
                    id="solution"
                    name="solution"
                    value={campaignData.solution}
                    onChange={handleInputChange}
                    placeholder="How does your product uniquely solve this problem? What's your approach?"
                    className="min-h-24 border-slate-200"
                    required
                  />
                  <p className="text-xs text-slate-500">
                    Highlight your unique approach, key features, and
                    competitive advantages
                  </p>
                </div>
              </div>

              {/* Optimization tips */}
              <div className="p-4 bg-emerald-50 border border-emerald-200 rounded-lg">
                <div className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-emerald-600 mt-0.5 flex-shrink-0" />
                  <div>
                    <h4 className="font-medium text-emerald-900">
                      Campaign Optimization Tips
                    </h4>
                    <ul className="text-sm text-emerald-700 mt-2 space-y-1">
                      <li>
                        • Clear problem-solution statements increase engagement
                        by 35%
                      </li>
                      <li>
                        • Specific product descriptions lead to more detailed
                        feedback
                      </li>
                      <li>
                        • Well-defined categories help with campaign discovery
                      </li>
                      <li>
                        • Active campaigns receive 3x more responses than drafts
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        );
      case 2:
        return (
          <Card className="border-slate-200/60 shadow-lg">
            <CardHeader className="bg-gradient-to-r from-primary/5 to-accent/5">
              <CardTitle className="flex items-center gap-3">
                <div className="p-2 bg-primary/10 rounded-lg">
                  <Target className="w-6 h-6 text-primary" />
                </div>
                Design Your Research Questions
              </CardTitle>
              <p className="text-slate-600 mt-2">
                Craft strategic questions that will provide actionable insights.
                Well-designed questions can increase response quality by up to
                60%.
              </p>
            </CardHeader>
            <CardContent className="p-8 space-y-6">
              <div className="space-y-4">
                {campaignData.questions.map((question, index: number) => (
                  <div
                    key={index}
                    className="p-6 bg-slate-50/50 border border-slate-200/60 rounded-xl space-y-4"
                  >
                    <div className="flex justify-between items-center">
                      <h4 className="font-semibold text-slate-900">
                        Research Question {index + 1}
                      </h4>
                      {campaignData.questions.length > 1 && (
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => removeQuestion(index)}
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      )}
                    </div>

                    <div className="space-y-2">
                      <label className="text-sm font-medium text-slate-700">
                        Question Text
                      </label>
                      <Input
                        placeholder="What specific insight do you want to gather?"
                        value={question.text}
                        onChange={(e) =>
                          updateQuestion(index, "text", e.target.value)
                        }
                        className="border-slate-200"
                      />
                      <p className="text-xs text-slate-500">
                        Be specific and actionable - avoid yes/no questions
                      </p>
                    </div>

                    <div className="space-y-2">
                      <label className="text-sm font-medium text-slate-700">
                        Response Format
                      </label>
                      <select
                        className="w-full p-3 border border-slate-200 rounded-lg bg-white"
                        value={question.type}
                        onChange={(e) =>
                          updateQuestion(index, "type", e.target.value)
                        }
                      >
                        <option value="text">Open-ended feedback</option>
                        <option value="emoji">Emoji sentiment rating</option>
                        <option value="multiple">
                          Multiple choice selection
                        </option>
                        <option value="rating">Numerical rating scale</option>
                      </select>
                      <p className="text-xs text-slate-500">
                        Choose the format that will give you the most valuable
                        insights
                      </p>
                    </div>

                    {/* Dynamic UI based on question type */}
                    {question.type && question.type !== "text" && (
                      <div className="pt-4 border-t border-slate-200">
                        {question.type === "multiple" && (
                          <div className="space-y-4">
                            <div className="flex items-center justify-between">
                              <label className="text-sm font-medium text-slate-700">
                                Multiple Choice Options
                              </label>
                              <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                onClick={() => addOption(index)}
                                className="text-primary hover:text-primary-700"
                              >
                                <Plus className="w-4 h-4 mr-1" />
                                Add Option
                              </Button>
                            </div>

                            <div className="space-y-2">
                              {question.options?.map(
                                (option: string, optionIndex: number) => (
                                  <div
                                    key={optionIndex}
                                    className="flex items-center gap-2"
                                  >
                                    <Input
                                      placeholder={`Option ${optionIndex + 1}`}
                                      value={option}
                                      onChange={(e) =>
                                        updateOption(
                                          index,
                                          optionIndex,
                                          e.target.value
                                        )
                                      }
                                      className="flex-1 border-slate-200"
                                    />
                                    {question.options.length > 2 && (
                                      <Button
                                        type="button"
                                        variant="ghost"
                                        size="sm"
                                        onClick={() =>
                                          removeOption(index, optionIndex)
                                        }
                                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                      >
                                        <Trash2 className="w-4 h-4" />
                                      </Button>
                                    )}
                                  </div>
                                )
                              )}
                            </div>

                            {(!question.options ||
                              question.options.length < 2) && (
                              <p className="text-xs text-amber-600">
                                Multiple choice questions require at least 2
                                options
                              </p>
                            )}
                          </div>
                        )}

                        {question.type === "emoji" && (
                          <div className="space-y-4">
                            <label className="text-sm font-medium text-slate-700">
                              Emoji Options (Optional - defaults will be used if
                              empty)
                            </label>

                            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                              {[
                                {
                                  emoji: "😍",
                                  label: "Love it!",
                                  default: true,
                                },
                                {
                                  emoji: "🙂",
                                  label: "Like it",
                                  default: true,
                                },
                                {
                                  emoji: "😐",
                                  label: "Neutral",
                                  default: true,
                                },
                                { emoji: "😕", label: "Unsure", default: true },
                                {
                                  emoji: "👎",
                                  label: "Not for me",
                                  default: true,
                                },
                              ].map((option, optionIndex) => (
                                <div
                                  key={optionIndex}
                                  className="flex items-center gap-2 p-3 border border-slate-200 rounded-lg bg-slate-50"
                                >
                                  <span className="text-2xl">
                                    {option.emoji}
                                  </span>
                                  <span className="text-sm font-medium text-slate-700">
                                    {option.label}
                                  </span>
                                  {option.default && (
                                    <span className="text-xs text-slate-500 ml-auto">
                                      Default
                                    </span>
                                  )}
                                </div>
                              ))}
                            </div>

                            <p className="text-xs text-slate-500">
                              These are the default emoji options. You can
                              customize them by adding custom options below.
                            </p>
                          </div>
                        )}

                        {question.type === "rating" && (
                          <div className="space-y-4">
                            <div className="grid grid-cols-2 gap-4">
                              <div className="space-y-2">
                                <label className="text-sm font-medium text-slate-700">
                                  Rating Scale
                                </label>
                                <select
                                  className="w-full p-3 border border-slate-200 rounded-lg bg-white"
                                  value={question.ratingScale || "5"}
                                  onChange={(e) =>
                                    updateQuestion(
                                      index,
                                      "ratingScale",
                                      e.target.value
                                    )
                                  }
                                >
                                  <option value="3">1-3 Scale</option>
                                  <option value="5">1-5 Scale</option>
                                  <option value="7">1-7 Scale</option>
                                  <option value="10">1-10 Scale</option>
                                </select>
                              </div>

                              <div className="space-y-2">
                                <label className="text-sm font-medium text-slate-700">
                                  Rating Labels
                                </label>
                                <select
                                  className="w-full p-3 border border-slate-200 rounded-lg bg-white"
                                  value={question.ratingLabels || "numbers"}
                                  onChange={(e) =>
                                    updateQuestion(
                                      index,
                                      "ratingLabels",
                                      e.target.value
                                    )
                                  }
                                >
                                  <option value="numbers">
                                    Numbers (1, 2, 3...)
                                  </option>
                                  <option value="stars">Stars (⭐)</option>
                                  <option value="custom">Custom Labels</option>
                                </select>
                              </div>
                            </div>

                            {question.ratingLabels === "custom" && (
                              <div className="space-y-2">
                                <label className="text-sm font-medium text-slate-700">
                                  Custom Labels (comma-separated)
                                </label>
                                <Input
                                  placeholder="e.g., Poor, Fair, Good, Very Good, Excellent"
                                  value={question.customLabels || ""}
                                  onChange={(e) =>
                                    updateQuestion(
                                      index,
                                      "customLabels",
                                      e.target.value
                                    )
                                  }
                                  className="border-slate-200"
                                />
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    )}

                    {/* Question validation */}
                    {question.text && question.type && (
                      <div className="p-3 bg-emerald-50 border border-emerald-200 rounded-lg">
                        <div className="flex items-start gap-2">
                          <CheckCircle className="w-4 h-4 text-emerald-600 mt-0.5 flex-shrink-0" />
                          <div className="text-sm text-emerald-700">
                            <span className="font-medium">
                              Question {index + 1} is ready!
                            </span>
                            {question.type === "multiple" &&
                              question.options &&
                              question.options.length >= 2 && (
                                <span>
                                  {" "}
                                  - {question.options.length} options configured
                                </span>
                              )}
                            {question.type === "rating" && (
                              <span>
                                {" "}
                                - {question.ratingScale || 5}-point scale
                                configured
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>

              <Button
                type="button"
                variant="outline"
                onClick={addQuestion}
                className="w-full gap-2 border-dashed border-2 py-8 text-slate-600 hover:text-slate-900 hover:border-slate-300"
              >
                <Plus className="w-5 h-5" />
                Add Research Question
              </Button>

              {/* Best practices */}
              <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <div className="flex items-start gap-3">
                  <Target className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
                  <div>
                    <h4 className="font-medium text-blue-900">
                      Research Best Practices
                    </h4>
                    <ul className="text-sm text-blue-700 mt-2 space-y-1">
                      <li>• Ask about specific behaviors and preferences</li>
                      <li>• Include questions about willingness to pay</li>
                      <li>• Gather feedback on key features and benefits</li>
                      <li>
                        • Limit to 3-5 questions for optimal completion rates
                      </li>
                      <li>
                        • Use multiple choice for quick responses, text for
                        detailed insights
                      </li>
                      <li>
                        • Emoji ratings work great for sentiment and first
                        impressions
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        );
      case 3:
        return (
          <Card className="border-slate-200/60 shadow-lg">
            <CardHeader className="bg-gradient-to-r from-primary/5 to-accent/5">
              <CardTitle className="flex items-center gap-3">
                <div className="p-2 bg-primary/10 rounded-lg">
                  <DollarSign className="w-6 h-6 text-primary" />
                </div>
                Define Your Pricing Strategy
              </CardTitle>
              <p className="text-slate-600 mt-2">
                Test different price points to understand your market's
                willingness to pay. This data is crucial for your go-to-market
                strategy.
              </p>
            </CardHeader>
            <CardContent className="p-8 space-y-6">
              <div className="space-y-4">
                {campaignData.pricingOptions.map((option, index: number) => (
                  <div
                    key={index}
                    className="p-6 bg-slate-50/50 border border-slate-200/60 rounded-xl space-y-4"
                  >
                    <div className="flex justify-between items-center">
                      <h4 className="font-semibold text-slate-900">
                        Pricing Tier {index + 1}
                      </h4>
                      {campaignData.pricingOptions.length > 1 && (
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => removePricingOption(index)}
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      )}
                    </div>

                    <div className="grid md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <label className="text-sm font-medium text-slate-700">
                          Price Point
                        </label>
                        <Input
                          placeholder="e.g., $9.99, $19.99, $49.99"
                          value={option.price}
                          onChange={(e) =>
                            updatePricingOption(index, "price", e.target.value)
                          }
                          className="border-slate-200"
                        />
                        <p className="text-xs text-slate-500">
                          Test realistic price points for your market
                        </p>
                      </div>
                      <div className="space-y-2">
                        <label className="text-sm font-medium text-slate-700">
                          What's Included
                        </label>
                        <Input
                          placeholder="e.g., Basic features, Pro features, Enterprise features"
                          value={option.description}
                          onChange={(e) =>
                            updatePricingOption(
                              index,
                              "description",
                              e.target.value
                            )
                          }
                          className="border-slate-200"
                        />
                        <p className="text-xs text-slate-500">
                          Describe the value proposition at this price
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              <Button
                type="button"
                variant="outline"
                onClick={addPricingOption}
                className="w-full gap-2 border-dashed border-2 py-8 text-slate-600 hover:text-slate-900 hover:border-slate-300"
              >
                <Plus className="w-5 h-5" />
                Add Pricing Tier
              </Button>

              {/* Pricing insights */}
              <div className="p-4 bg-purple-50 border border-purple-200 rounded-lg">
                <div className="flex items-start gap-3">
                  <DollarSign className="w-5 h-5 text-purple-600 mt-0.5 flex-shrink-0" />
                  <div>
                    <h4 className="font-medium text-purple-900">
                      Pricing Validation Insights
                    </h4>
                    <ul className="text-sm text-purple-700 mt-2 space-y-1">
                      <li>• Test 2-3 price points to find the sweet spot</li>
                      <li>• Include a "free" option to understand demand</li>
                      <li>
                        • Ask about feature preferences at each price level
                      </li>
                      <li>
                        • This data will inform your final pricing strategy
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        );
      case 4:
        return (
          <Card className="border-slate-200/60 shadow-lg">
            <CardHeader className="bg-gradient-to-r from-primary/5 to-accent/5">
              <CardTitle className="flex items-center gap-3">
                <div className="p-2 bg-primary/10 rounded-lg">
                  <Settings className="w-6 h-6 text-primary" />
                </div>
                Configure Your Campaign
              </CardTitle>
              <p className="text-slate-600 mt-2">
                Set up your campaign visibility and visual presentation to
                maximize engagement and response rates.
              </p>
            </CardHeader>
            <CardContent className="p-8 space-y-8">
              {/* Privacy Settings */}
              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-slate-900">
                  Campaign Visibility
                </h3>
                <div className="space-y-3">
                  <div className="flex items-start space-x-3 p-4 border border-slate-200 rounded-xl hover:bg-slate-50/50 transition-colors">
                    <input
                      type="radio"
                      id="public"
                      name="isPrivate"
                      value="false"
                      checked={!campaignData.isPrivate}
                      onChange={(e) =>
                        setCampaignData({
                          ...campaignData,
                          isPrivate: e.target.value === "true",
                        })
                      }
                      className="mt-1"
                    />
                    <div className="space-y-1">
                      <label
                        htmlFor="public"
                        className="text-base font-medium flex items-center gap-2"
                      >
                        <span className="text-green-600">🔓</span>
                        Public Campaign
                      </label>
                      <p className="text-sm text-slate-600">
                        Your campaign will be discoverable in our marketplace,
                        increasing exposure and potential responses.
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start space-x-3 p-4 border border-slate-200 rounded-xl hover:bg-slate-50/50 transition-colors">
                    <input
                      type="radio"
                      id="private"
                      name="isPrivate"
                      value="true"
                      checked={campaignData.isPrivate}
                      onChange={(e) =>
                        setCampaignData({
                          ...campaignData,
                          isPrivate: e.target.value === "true",
                        })
                      }
                      className="mt-1"
                    />
                    <div className="space-y-1">
                      <label
                        htmlFor="private"
                        className="text-base font-medium flex items-center gap-2"
                      >
                        <span className="text-amber-600">🔒</span>
                        Private Campaign
                      </label>
                      <p className="text-sm text-slate-600">
                        Only accessible via direct links you share. Perfect for
                        confidential or targeted validation.
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Banner Image */}
              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-slate-900">
                  Campaign Visual
                </h3>

                {/* Category-specific banner selection */}
                {campaignData.category && (
                  <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                    <div className="flex items-start gap-2">
                      <div className="p-1 bg-blue-100 rounded">
                        <Image className="w-4 h-4 text-blue-600" />
                      </div>
                      <div>
                        <h4 className="font-medium text-blue-900 text-sm">
                          Category-Specific Banners
                        </h4>
                        <p className="text-xs text-blue-700 mt-1">
                          Showing banners tailored for your "
                          {campaignData.category}" category.
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Preview */}
                <div className="relative w-full h-32 overflow-hidden rounded-xl shadow-md">
                  <img
                    src={campaignData.bannerImage}
                    alt="Selected banner"
                    className="w-full h-full object-cover"
                  />
                  {bannerImages.find(
                    (img) => img.url === campaignData.bannerImage
                  )?.category && (
                    <div className="absolute top-2 left-2 bg-black/70 text-white px-2 py-1 rounded text-xs">
                      {
                        bannerImages.find(
                          (img) => img.url === campaignData.bannerImage
                        )?.category
                      }
                    </div>
                  )}
                </div>

                {/* Options */}
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <h4 className="text-sm font-medium text-slate-700">
                      {campaignData.category
                        ? `${campaignData.category} Banners`
                        : "All Banners"}
                    </h4>
                    {campaignData.category && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          const relevantBanners = bannerImages.filter(
                            (img) => img.category === campaignData.category
                          );
                          if (relevantBanners.length > 0) {
                            setCampaignData({
                              ...campaignData,
                              bannerImage: relevantBanners[0].url,
                            });
                          }
                        }}
                        className="text-xs text-primary hover:text-primary-700"
                      >
                        Auto-select first
                      </Button>
                    )}
                  </div>

                  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2 sm:gap-3">
                    {(campaignData.category
                      ? bannerImages.filter(
                          (img) => img.category === campaignData.category
                        )
                      : bannerImages
                    ).map((image) => (
                      <div
                        key={image.id}
                        onClick={() =>
                          setCampaignData({
                            ...campaignData,
                            bannerImage: image.url,
                          })
                        }
                        className={`cursor-pointer rounded-lg overflow-hidden border-2 transition-all relative ${
                          campaignData.bannerImage === image.url
                            ? "border-primary ring-2 ring-primary/20"
                            : "border-slate-200"
                        }`}
                      >
                        <img
                          src={image.thumbnail}
                          alt={image.name}
                          className="w-full h-12 sm:h-16 object-cover"
                        />
                        {campaignData.bannerImage === image.url && (
                          <div className="absolute inset-0 bg-primary/20 flex items-center justify-center">
                            <CheckCircle className="text-primary w-4 h-4 sm:w-6 sm:h-6 bg-white rounded-full" />
                          </div>
                        )}
                        <div className="absolute bottom-0 left-0 right-0 bg-black/70 text-white p-1">
                          <p className="text-xs truncate">{image.name}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Campaign optimization tips */}
              <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
                <div className="flex items-start gap-3">
                  <Settings className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
                  <div>
                    <h4 className="font-medium text-amber-900">
                      Campaign Optimization
                    </h4>
                    <ul className="text-sm text-amber-700 mt-2 space-y-1">
                      <li>
                        • Public campaigns receive 3x more responses on average
                      </li>
                      <li>
                        • Category-specific visuals increase engagement by 40%
                      </li>
                      <li>• You can change visibility settings anytime</li>
                      <li>
                        • Professional banners improve campaign credibility
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        );
      case 5:
        return (
          <Card className="border-slate-200/60 shadow-lg">
            <CardHeader className="bg-gradient-to-r from-primary/5 to-accent/5">
              <CardTitle className="flex items-center gap-3">
                <div className="p-2 bg-primary/10 rounded-lg">
                  <Eye className="w-6 h-6 text-primary" />
                </div>
                Review & Update Campaign
              </CardTitle>
              <p className="text-slate-600 mt-2">
                Review your updated campaign details and save changes to improve
                your validation results.
              </p>
            </CardHeader>
            <CardContent className="p-8 space-y-8">
              {/* Campaign Preview */}
              <div className="relative overflow-hidden rounded-xl h-40">
                <img
                  src={campaignData.bannerImage}
                  alt="Campaign banner"
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent flex flex-col justify-end p-6">
                  <h2 className="text-2xl font-bold text-white">
                    {campaignData.title}
                  </h2>
                  <p className="text-white/80">{campaignData.tagline}</p>
                </div>
              </div>

              {/* Summary Cards */}
              <div className="grid md:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <h3 className="font-semibold text-slate-900 border-b pb-2">
                    Updated Product Details
                  </h3>
                  <div className="space-y-3 text-sm">
                    <div>
                      <span className="font-medium text-slate-600">
                        Product Name:
                      </span>
                      <p className="text-slate-900">{campaignData.title}</p>
                    </div>
                    <div>
                      <span className="font-medium text-slate-600">
                        Value Proposition:
                      </span>
                      <p className="text-slate-900 line-clamp-3">
                        {campaignData.tagline}
                      </p>
                    </div>
                    <div>
                      <span className="font-medium text-slate-600">
                        Product Overview:
                      </span>
                      <p className="text-slate-900 line-clamp-3">
                        {campaignData.description}
                      </p>
                    </div>
                    <div>
                      <span className="font-medium text-slate-600">
                        Campaign Status:
                      </span>
                      <p className="text-slate-900">{campaignData.status}</p>
                    </div>
                  </div>
                </div>

                <div className="space-y-4">
                  <h3 className="font-semibold text-slate-900 border-b pb-2">
                    Problem-Solution Framework
                  </h3>
                  <div className="space-y-3 text-sm">
                    <div>
                      <span className="font-medium text-slate-600">
                        Market Problem:
                      </span>
                      <p className="text-slate-900 line-clamp-3">
                        {campaignData.problemStatement}
                      </p>
                    </div>
                    <div>
                      <span className="font-medium text-slate-600">
                        Your Solution:
                      </span>
                      <p className="text-slate-900 line-clamp-3">
                        {campaignData.solution}
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Additional Summary Cards */}
              <div className="grid md:grid-cols-3 gap-6">
                <div className="space-y-4">
                  <h3 className="font-semibold text-slate-900 border-b pb-2">
                    Validation Strategy
                  </h3>
                  <div className="space-y-3 text-sm">
                    <div>
                      <span className="font-medium text-slate-600">
                        Research Questions:
                      </span>
                      <p className="text-slate-900">
                        {campaignData.questions.length} question(s)
                      </p>
                    </div>
                    <div>
                      <span className="font-medium text-slate-600">
                        Pricing Tiers:
                      </span>
                      <p className="text-slate-900">
                        {campaignData.pricingOptions.length} option(s)
                      </p>
                    </div>
                  </div>
                </div>

                <div className="space-y-4">
                  <h3 className="font-semibold text-slate-900 border-b pb-2">
                    Campaign Settings
                  </h3>
                  <div className="space-y-3 text-sm">
                    <div>
                      <span className="font-medium text-slate-600">
                        Visibility:
                      </span>
                      <p className="text-slate-900">
                        {campaignData.isPrivate ? "Private" : "Public"}
                      </p>
                    </div>
                    <div>
                      <span className="font-medium text-slate-600">
                        Category:
                      </span>
                      <p className="text-slate-900">
                        {campaignData.category || "Not set"}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="space-y-4">
                  <h3 className="font-semibold text-slate-900 border-b pb-2">
                    Visual Presentation
                  </h3>
                  <div className="space-y-3 text-sm">
                    <div>
                      <span className="font-medium text-slate-600">
                        Banner Image:
                      </span>
                      <p className="text-slate-900">
                        {campaignData.bannerImage
                          ? "Custom selected"
                          : "Default"}
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Update notice */}
              <div className="p-4 sm:p-6 bg-gradient-to-r from-primary/10 to-accent/10 rounded-xl border border-primary/20">
                <div className="flex flex-col sm:flex-row items-start gap-3 sm:gap-4">
                  <div className="p-2 bg-primary/20 rounded-lg flex-shrink-0">
                    <Save className="w-5 h-5 sm:w-6 sm:h-6 text-primary" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h4 className="font-semibold text-slate-900 mb-2 text-sm sm:text-base">
                      Ready to Update Your Campaign!
                    </h4>
                    <p className="text-slate-600 mb-3 text-xs sm:text-sm leading-relaxed">
                      Your campaign will be updated immediately with the new
                      information. These changes will help improve engagement
                      and provide more accurate validation insights.
                    </p>
                    <div className="flex flex-col sm:flex-row items-start sm:items-center gap-2 sm:gap-4 text-xs sm:text-sm text-slate-500">
                      <div className="flex items-center gap-1">
                        <CheckCircle className="w-3 h-3 sm:w-4 sm:h-4 text-emerald-500 flex-shrink-0" />
                        <span>Instant updates</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <CheckCircle className="w-3 h-3 sm:w-4 sm:h-4 text-emerald-500 flex-shrink-0" />
                        <span>Improved engagement</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <CheckCircle className="w-3 h-3 sm:w-4 sm:h-4 text-emerald-500 flex-shrink-0" />
                        <span>Better insights</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        );
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50/30 to-indigo-50/20">
      <NavBar />

      <div className="container mx-auto px-4 pt-24 pb-16">
        <div className="max-w-4xl mx-auto">
          {/* Back Button */}
          <motion.div
            className="mb-6"
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.3 }}
          >
            <Button
              variant="ghost"
              onClick={() => navigate(`/campaign/${id}`)}
              className="text-slate-600 hover:text-slate-900 hover:bg-slate-100"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Campaign
            </Button>
          </motion.div>

          {/* Enhanced Header */}
          <motion.div
            className="text-center mb-12"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <div className="inline-flex items-center gap-2 bg-primary/10 text-primary px-4 py-2 rounded-full text-sm font-medium mb-4">
              <TrendingUp className="w-4 h-4" />
              Campaign Optimization
            </div>
            <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-slate-900 mb-4">
              Optimize Your Campaign
            </h1>
            <p className="text-base sm:text-lg md:text-xl text-slate-600 max-w-3xl mx-auto leading-relaxed px-4 sm:px-0">
              Improve your validation campaign to get better insights and higher
              engagement rates. Small changes can lead to significant
              improvements in response quality.
            </p>
          </motion.div>

          {/* Enhanced Progress Steps */}
          <motion.div
            className="mb-8 sm:mb-12 px-2 sm:px-0"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
          >
            <div className="flex justify-between items-center relative">
              {/* Progress line */}
              <div className="absolute top-1/3 left-0 right-0 h-0.5 bg-slate-200 -translate-y-1/2 -z-10" />
              <div
                className="absolute top-1/3 left-0 h-0.5 bg-gradient-to-r from-primary to-accent -translate-y-1/2 transition-all duration-500 ease-out"
                style={{
                  width: `${((currentStep - 1) / (steps.length - 1)) * 100}%`,
                }}
              />

              {steps.map((step) => (
                <div
                  key={step.id}
                  className="flex flex-col items-center relative z-10"
                >
                  <motion.div
                    className={`w-8 h-8 sm:w-10 sm:h-10 md:w-12 md:h-12 rounded-full border-2 flex items-center justify-center transition-all ${
                      currentStep >= step.id
                        ? "bg-primary border-transparent text-white shadow-lg"
                        : "bg-white border-slate-200 text-slate-400"
                    }`}
                    whileHover={{ scale: 1.05 }}
                  >
                    {currentStep > step.id ? (
                      <CheckCircle className="w-3 h-3 sm:w-4 sm:h-4 md:w-5 md:h-5" />
                    ) : (
                      <step.icon className="w-3 h-3 sm:w-4 sm:h-4 md:w-5 md:h-5" />
                    )}
                  </motion.div>
                  <div className="mt-1 sm:mt-2 text-center">
                    <p
                      className={`text-xs sm:text-sm font-medium ${
                        currentStep >= step.id
                          ? "text-slate-900"
                          : "text-slate-500"
                      }`}
                    >
                      {step.title}
                    </p>
                    <p className="text-xs text-slate-400 hidden sm:block">
                      {step.description}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </motion.div>

          {/* Content */}
          <motion.div
            key={currentStep}
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.3 }}
          >
            {renderStepContent()}
          </motion.div>

          {/* Enhanced Navigation */}
          <motion.div
            className="flex flex-col sm:flex-row justify-between items-center gap-4 sm:gap-0 mt-6 sm:mt-8 px-4 sm:px-0"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
          >
            <Button
              variant="outline"
              onClick={handlePrevStep}
              disabled={currentStep === 1}
              className="gap-2 w-full sm:w-auto text-sm sm:text-base"
            >
              <ArrowLeft className="w-4 h-4" />
              Previous
            </Button>

            <div className="text-xs sm:text-sm text-slate-500 order-first sm:order-none">
              Step {currentStep} of {steps.length}
            </div>

            {currentStep < steps.length ? (
              <Button
                onClick={handleNextStep}
                className="gap-2 w-full sm:w-auto text-sm sm:text-base"
              >
                Next
                <ArrowRight className="w-4 h-4" />
              </Button>
            ) : (
              <Button
                onClick={handleSaveCampaign}
                disabled={updateCampaignMutation.isPending}
                className="gap-2 bg-primary hover:bg-primary/90 w-full sm:w-auto text-sm sm:text-base"
              >
                {updateCampaignMutation.isPending ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    <span className="hidden sm:inline">Updating...</span>
                    <span className="sm:hidden">Updating</span>
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4" />
                    <span className="hidden sm:inline">Update Campaign</span>
                    <span className="sm:hidden">Update</span>
                  </>
                )}
              </Button>
            )}
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default EditCampaign;
